import React, { useRef } from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Button, { ILink } from 'tbk-components/src/components/Button';
import { IImage } from 'tbk-components/src/components/Image';
import { IVideoProps } from 'tbk-components/src/components/Video';
import Icon from 'tbk-components/src/components/Icon';
import AdvancedMedia from '../AdvancedMedia';

export interface IResourceCardSpecs {
	label?: string;
	description?: string;
}

export interface IFilterTag {
	label?: string;
	taxonomy?: string;
	term?: string;
}

/**
 * @usePostData
 * @keepallproperties
 * @post_type any
 */
export interface IResourceCardProps extends IBaseElementProps {
	/**
	 * This post_type should likely match the cardType as well
	 *
	 * @noUI
	 */
	post_type?: string;
	heading?: string;
	/**
	 * Useful for showing Time To Read, Date Posted, etc.
	 */
	eyebrow?: string;
	/**
	 * Text
	 * @control richtext
	 */
	content?: string;
	cta?: ILink;
	textCta?: ILink;
	/**
	 * This image will be rendered if Video URL and Oembed are empty.
	 * The image aspect ratio is decided by Card Type below.
	 */
	image?: IImage;
	/**
	 *
	 * @noUI
	 */
	extra_features?: {
		[key: string]: any;
	};
	/**
	 * Some Card Types define the media size on their own (Whitepaper images are set to 'contain' by default, most are set to 'cover'). But this will overwrite it.
	 */
	mediaSize?: 'default' | 'cover' | 'contain';
	video?: IVideoProps;
	logo?: IImage;
	cardType?:
		| 'Default'
		| 'Webinar'
		| 'Blog'
		| 'White Paper'
		| 'Ebook'
		| 'Brochure'
		| 'Infographic'
		| 'News'
		| 'Video'
		| 'Case Study'
		| 'Event'
		| 'Product'
		| 'Product Narrow'
		| 'Search';
	cardTypeLabel?: string;
	tags?: ILink[];
	specs?: IResourceCardSpecs[];
	tinyCard?: boolean;
	/**
	 * There is a specific card layout for when all cards are white papers.
	 *
	 * @noUI
	 */
	allWide?: boolean;
	/**
	 *
	 * @noUI
	 */
	allSearchList?: boolean;
	/**
	 * This should be passed down from the Listing component on all cards and not toggled individually.
	 *
	 * @noUI
	 */
	showTypeLabel?: boolean;
	/**
	 * This should be passed down from the Listing component on all cards and not toggled individually.
	 *
	 * @noUI
	 */
	hideCardExcerpt?: boolean;
	/**
	 * This is for optional button filter tags. There is a separate list of tags so Content can still get the ILink interface.
	 *
	 * @noUI
	 */
	filterTags?: IFilterTag[];
	/*
	 * @noUI
	 */
	onFilterTagClick?: (event: any) => any;
	headerSize?: 'small' | 'medium' | 'large';
	/*
	 * This is for the Product Compare feature. If enabled, a checkbox will appear on the card. And this will be wired down from the Index listing.
	 * @noUI
	 */
	enableProductCompare?: boolean;
	/**
	 * This is here so that the parent component can control the state of the product card.
	 * And potentially maintain its state on page refresh.
	 */
	productForCompare?: boolean;
	onProductCompareChange?: Function;
	productCompareLabel?: string;
}

const ResourceCard: React.FC<IResourceCardProps> = ({
	classNames = [],
	headerSize = 'medium',
	content,
	className,
	eyebrow,
	heading,
	cta,
	textCta,
	image,
	mediaSize = 'default',
	video,
	logo,
	cardType = 'Blog',
	cardTypeLabel,
	allWide = false,
	tags = [],
	extra_features,
	filterTags = [],
	specs = [],
	showTypeLabel = false,
	hideCardExcerpt = false,
	tinyCard = false,
	allSearchList = false,
	onFilterTagClick,
	enableProductCompare = false,
	productForCompare = false,
	onProductCompareChange = () => {},
	productCompareLabel,
}) => {
	// Based off the Card Type, it will ultimately funnel into these four cards:
	// 1. Text Card (Blogs, News, Case Studies, Events)
	// 2. Wide Card (White Papers, but ONLY if all cards are White Papers.)
	// 3. Wallpaper Card (Webinars, Video)
	// 4. Video Card
	// 5. Product Card (Product)
	// 6. Search Card (Forced on search results, usually used with allSearchList as well)

	let cardLayout;
	const cardRef = useRef(null);

	if (allSearchList) {
		cardType = 'Search';
	}

	if (['Upcoming Webinar', 'On-Demand Webinar', 'Video'].includes(cardType)) {
		cardLayout = 'wallpaper';
	} else {
		cardLayout = 'text';
	}

	if (
		(cardType === 'White Paper' ||
			cardType === 'Ebook' ||
			cardType === 'Infographic' ||
			cardType === 'Brochure') &&
		allWide
	) {
		cardLayout = 'wide';
	}

	const genMediaSize = mediaSize === 'default' ? null : mediaSize;

	const simulateMediaClick = (event) => {
		if (!cardRef.current) return;

		// If event.target is not an anchor or button element, simulate a click on the advanced media element
		// We want to keep any tag functionality as normal.
		const mediaElement = cardRef.current.querySelector(
			'.advanced-media__video .btn-play',
		);
		if (
			mediaElement &&
			event.target.tagName !== 'A' &&
			event.target.tagName !== 'BUTTON'
		) {
			event.preventDefault();

			// When click, simulate a click on the advanced media element to open up the video
			mediaElement.click();
		}
	};

	// Sometimes Content may or may not include the iframe in the Video field. Account for that.
	if (video && video?.oembed) {
		const iframeMatch = video?.oembed.match(/<iframe.*<\/iframe>/);
		if (!iframeMatch) {
			const storedVideoEmbed = video?.oembed;
			video.oembed = `<iframe width="560" height="315" src="${storedVideoEmbed}" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>`;
		}
	}

	const core = (
		<div
			className={cn(
				`calian-resource-card__content z-1 d-flex w-100 h-100 ${
					!allWide
						? 'flex-column'
						: 'flex-column flex-sm-row align-items-start'
				}`,
				video?.oembed && 'has-video-embed',
				allWide &&
					(cardType === 'Product' ||
						cardType === 'Product Narrow' ||
						cardType === 'Search') &&
					'w-100',
				allSearchList
					? 'flex-row-reverse justify-content-between w-100 flex-sm-row-reverse gap-3 gap-xl-5'
					: 'gap-5',
			)}
		>
			{showTypeLabel && (
				<div className="calian-resource-card__type-tag-label z-3 position-absolute top-0 start-0 py-2 px-4 bg-secondary text-white">
					{cardTypeLabel}
				</div>
			)}
			{cardLayout === 'wallpaper' && !video && (
				<div className="calian-resource-card__overlay position-absolute top-0 start-0 w-100 h-100 z-1" />
			)}
			<AdvancedMedia
				classNames={[
					cardLayout == 'wallpaper' &&
						'position-absolute top-0 start-0 h-100 w-100',
					'flex-shrink-0 ',
					cardLayout == 'text' &&
						cardType !== 'Case Study' &&
						!(
							cardType === 'Product' ||
							cardType === 'Product Narrow' ||
							cardType === 'Search'
						) &&
						'mw-220px',
					cardType !== 'White Paper' &&
					cardType !== 'Ebook' &&
					cardType !== 'Infographic' &&
					cardType !== 'Brochure' &&
					!(cardType === 'Product' || cardType === 'Product Narrow')
						? 'rounded-3 rounded-md-5'
						: 'mx-auto w-100',
					(cardType === 'White Paper' ||
						cardType === 'Ebook' ||
						cardType === 'Infographic' ||
						cardType === 'Brochure') &&
						!allWide &&
						'mt-md-5',
					cardType == 'Product' && 'p-1 mw-160px',
					cardType === 'Product Narrow' && 'mt-2',
					(cardType === 'Search' || allSearchList) &&
						'w-100 mw-64px mw-md-120px p-0',
				]}
				size={
					cardType === 'Case Study'
						? '16:9'
						: cardType === 'White Paper'
						? 'Default'
						: cardType === 'Ebook'
						? 'Default'
						: cardType === 'Brochure'
						? 'Default'
						: cardType === 'Infographic'
						? 'Default'
						: cardLayout === 'wallpaper'
						? 'Default'
						: cardType === 'Product Narrow'
						? 'Default'
						: '1:1'
				}
				mediaSize={
					genMediaSize ||
					(cardType === 'White Paper' ||
					cardType === 'Ebook' ||
					cardType === 'Brochure' ||
					cardType === 'Infographic' ||
					cardType === 'Product Narrow'
						? 'contain'
						: 'cover')
				}
				image={image}
				disablePlayControls
				video={video}
			/>
			<div
				className={cn(
					'calian-resource-card__copy',
					video?.oembed &&
						cardLayout === 'wallpaper' &&
						'video-popout',
					'd-inline-flex flex-wrap h-100 flex-column z-2 flex-grow-1',
					cardLayout === 'wallpaper' &&
						'text-white mb-0 mt-auto justify-content-end',
					cardLayout === 'text' &&
						cardType !== 'Case Study' &&
						'pe-2',
					allWide &&
						(cardType === 'Product' ||
							cardType === 'Product Narrow' ||
							cardType === 'Search') &&
						'w-100',
				)}
			>
				{(eyebrow || heading) && (
					<div
						className={cn(
							'calian-resource-card__header d-flex',
							cardLayout === 'wallpaper'
								? 'flex-column-reverse gap-3'
								: 'flex-column',
							(cardType === 'White Paper' ||
								cardType === 'Ebook' ||
								cardType === 'Brochure' ||
								cardType === 'Infographic') &&
								!allWide &&
								'mt-auto mb-0',
						)}
					>
						{eyebrow && (
							<div
								className={cn(
									'calian-resource-card__eyebrow mb-0',
									cardLayout === 'wallpaper' &&
										'h6 text-white',
								)}
							>
								{eyebrow}
							</div>
						)}
						{heading && (
							<h4
								className={cn(
									'calian-resource-card__heading mb-0',
									cardLayout === 'wallpaper' && 'text-white',
									cardType === 'Product' && 'h5',
									cardType === 'Product Narrow' && 'h5',
									headerSize,
								)}
							>
								{heading}
								{cardType === 'Search' && (
									<Icon name="arrow-right" />
								)}
							</h4>
						)}
					</div>
				)}
				{tags?.length > 0 && (
					<ul
						className={cn(
							'calian-resource-card__tags d-flex p-0 list-style-none flex-wrap gap-2 mt-3 mb-0',
							allSearchList && 'd-none d-lg-flex',
						)}
					>
						{tags.slice(0, 5).map((tag, index) => (
							<li
								key={index}
								className="calian-resource-card__tag text-decoration-none"
							>
								<Button
									className={cn(
										'text-decoration-none btn btn-sm px-3',
										cardLayout === 'text' &&
											'border border-1 border-color-light',
										cardType !== 'Search' && 'bg-light',
									)}
									target={tag.target}
									href={tag.href}
									label={tag.label}
								/>
							</li>
						))}
					</ul>
				)}
				{filterTags?.length > 0 && (
					<ul
						className={cn(
							'calian-resource-card__tags calian-resource-card__tags--filters d-flex p-0 list-style-none flex-wrap gap-2 mt-3 mb-0',
							allSearchList && 'd-none d-lg-flex',
						)}
					>
						{filterTags.map((filter, index) => (
							<li
								key={index}
								className="calian-resource-card__tag calian-resource-card__tag--filters text-decoration-none"
							>
								<button
									className={cn(
										'text-decoration-none btn btn-sm px-3',
										cardLayout === 'text' &&
											'border border-1 border-color-light',
										cardType !== 'Search' && 'bg-light',
									)}
									data-taxonomy={filter.taxonomy}
									data-term={filter.term}
									onClick={onFilterTagClick || null}
								>
									{filter.label}
								</button>
							</li>
						))}
					</ul>
				)}
				{!hideCardExcerpt && content && content !== '<br>' && (
					<div
						className={cn(
							'calian-resource-card__excerpt mt-3',
							allWide &&
								(cardType === 'Product' ||
									cardType === 'Product Narrow') &&
								'pe-4 pe-lg-6',
						)}
						dangerouslySetInnerHTML={{
							__html: content,
						}}
					/>
				)}
				{specs?.length > 0 && (
					<ul
						className={cn(
							'calian-resource-card__specs mt-3 p-0 d-flex gap-1 flex-column',
							allWide &&
								(cardType === 'Product' ||
									cardType === 'Product Narrow') &&
								'pe-4 pe-lg-6',
						)}
					>
						{specs.map((spec, index) => (
							<li
								className={cn(
									'calian-resource-card__spec d-inline small',
									index === 0
										? 'mb-2 d-flex flex-column'
										: 'mb-0',
								)}
							>
								{spec.label && (
									<b>
										{spec.label}
										{spec.description && ': '}
									</b>
								)}
								{spec.description && spec.description}
							</li>
						))}
					</ul>
				)}
				{logo && (
					<img
						className="calian-resource-card__logo mw-156px mt-auto mb-0 pt-4 w-100 h-auto object-fit-contain"
						src={logo.src}
						alt={logo.alt}
					/>
				)}
				<div className={`calian-resource-card__footer d-flex gap-4 justify-content-between${(cardLayout !== 'wallpaper' && cardLayout !== 'Video') && ' mt-auto'}`}>
					{!allSearchList &&
						((cta?.label && cta?.href) ||
							(textCta?.label && textCta?.href)) && (
							<div
								className={cn(
									'calian-resource-card__ctas d-flex flex-wrap gap-3',
									cardType === 'Product' ||
										cardType === 'Product Narrow'
										? 'pt-3'
										: ' pt-4',
									!(
										allWide &&
										(cardType === 'Product' ||
											cardType === 'Product Narrow')
									) &&
										cardLayout === 'text' &&
										'justify-content-between mb-0',
									allWide &&
										(cardType === 'Product' ||
											cardType === 'Product Narrow')
										? 'position-absolute bottom-0 end-0 mb-4 me-5'
										: ' align-items-center',
									((!logo &&
										cardLayout === 'text' &&
										cardType !== 'Event' &&
										cardType !== 'White Paper' &&
										cardType !== 'Ebook') ||
										allWide) &&
										'mt-auto',
								)}
							>
								{cta?.href &&
								!(cardLayout == 'wallpaper' && video?.oembed) ? (
									<>
										{(cardLayout === 'text' &&
											cardType !== 'Event') ||
										cardType == 'White Paper' ||
										cardType == 'Ebook' ||
										cardType == 'Infographic' ||
										cardType == 'Brochure' ? (
											<div className="d-flex gap-2">
											{video?.oembed ? cta?.label : ''}
											<span className="text-decoration-none arrow-btn text-primary h4 mb-0">
												<Icon name="arrow-right" />
											</span>
											</div>
										) : (
											<Button
												size="sm"
												type={
													cardType == 'Event'
														? 'secondary'
														: 'primary'
												}
												{...cta}
											/>
										)}
									</>
								) : null}
								{textCta?.label && textCta?.href && (
									<Button
										href={textCta.href}
										label={textCta.label}
										target={textCta.target}
										className={cn(
											'mb-0',
											'calian-resource-card__text-cta ',
											cardLayout === 'wallpaper'
												? 'text-white'
												: 'text-primary',
										)}
									/>
								)}
							</div>
						)}

					{enableProductCompare && (
						<div className="compare-checkbox mt-5 d-flex align-items-center gap-2">
							<span className="-translate-y-[1px] text-sm">
								{productCompareLabel || 'Compare'}
							</span>
							<input
								type="checkbox"
								checked={productForCompare}
								onChange={(e) => onProductCompareChange(e)}
								aria-label={productCompareLabel || 'Compare'}
							/>
						</div>
					)}
				</div>
			</div>
		</div>
	);
	return (
		<div
			ref={cardRef}
			className={cn(
				'calian-resource-card',
				`calian-resource-card--${cardType
					.replace(/\s+/g, '-')
					.toLowerCase()}`,
				`calian-resource-card--${cardLayout}`,
				tinyCard && 'calian-resource-card--tiny-layout',
				className || [
					...classNames,
					'overflow-hidden rounded-5 bg-white position-relative d-flex h-100',
					allWide &&
					(cardType === 'Product' ||
						cardType === 'Product Narrow' ||
						cardType === 'Search')
						? 'mw-none'
						: cardLayout === 'wide'
						? 'mw-xl-604px'
						: allSearchList
						? 'mw-xl-none'
						: 'mw-xl-392px',
				],
			)}
		>
			{React.createElement(
				cta?.href && !(video && video?.oembed) ? Button : 'div',
				{
					className: `calian-resource-card__container calian-resource-card__${
						cta?.href ? 'link' : 'no-link'
					} z-0 rounded-5 overflow-hidden w-100 d-flex text-decoration-none p-4 ${
						cardLayout === 'text' &&
						!allSearchList &&
						'p-md-5 border border-1 border-color-light'
					} ${cardLayout === 'wide' && 'p-md-5'} ${
						showTypeLabel && 'pt-7 pt-md-7'
					} ${video && video?.oembed && 'video-popout'}`,
					children: core,
					href: cta?.href,
					target: cta?.target,
					'aria-label': heading,
					title: heading,
					onClick:
						cta?.href && !(video && video?.oembed)
							? null
							: simulateMediaClick,
				},
			)}
		</div>
	);
};

export default ResourceCard;
